import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

const itemClass = (isActive) =>
    isActive
        ? 'dark:text-[#41A6D7] text-[#3590BA] flex items-center text-xsm font-semibold border-b-2 border-[#3590BA] dark:border-[#41A6D7] pb-0.5 transition-all duration-200'
        : 'dark:text-gray-300 text-gray-600 flex items-center text-xsm hover:text-[#3590BA] dark:hover:text-[#41A6D7] hover:underline transition-all duration-200';

const MenuGroup = ({ item }) => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const childActive = item.children.some((child) => location.pathname === child.to);

    useEffect(() => {
        const onPointerDown = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    return (
        <li className="relative shrink-0" ref={ref}>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className={itemClass(childActive)}
            >
                <item.icon className="mr-1.5 w-4 h-4 stroke-[1.5] shrink-0" />
                <span className="whitespace-nowrap">{item.label}</span>
                <ChevronDown className={clsx('ml-1 w-4 h-4 stroke-[1.5] transition-transform', open && 'rotate-180')} />
            </button>
            {open && (
                <ul
                    role="menu"
                    className="absolute left-0 top-full mt-2 min-w-[22rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 z-50"
                >
                    {item.children.map((child) => (
                        <li key={child.to} role="none">
                            <NavLink
                                to={child.to}
                                role="menuitem"
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    clsx(
                                        'flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap',
                                        isActive
                                            ? 'text-[#3590BA] dark:text-[#41A6D7] font-semibold bg-gray-50 dark:bg-gray-700/60'
                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-[#3590BA] dark:hover:text-[#41A6D7]'
                                    )
                                }
                            >
                                <child.icon className="w-4 h-4 stroke-[1.5] shrink-0" />
                                {child.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

const NavigationMenu = ({ items }) => {
    return (
        <div className="flex items-center w-full">
            <ul className={clsx('flex flex-row items-center justify-between w-full font-medium mt-0 gap-x-3 xl:gap-x-4', 'bg-white dark:bg-gray-800')}>
                {items.map((item) =>
                    item.children ? (
                        <MenuGroup key={item.id || item.label} item={item} />
                    ) : (
                        <li key={item.to} className="shrink-0">
                            <NavLink to={item.to} className={({ isActive }) => itemClass(isActive)}>
                                <item.icon className="mr-1.5 w-4 h-4 stroke-[1.5] shrink-0" />
                                <span className="whitespace-nowrap">{item.label}</span>
                            </NavLink>
                        </li>
                    )
                )}
            </ul>
        </div>
    );
};

export default NavigationMenu;
